import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useAdminConnectorGeoFilters,
  type AdminConnectorGeoFilter,
} from "@/features/admin";
import { useConnectors } from "@/features/connectors/hooks/use-connectors";
import { useClientId } from "@/features/utils/api-scope";
import { useState } from "react";
import { TenantUsers } from "../../provisioning/components/tenant-users";
import { GeoFilterDialog } from "../../telephony/components/geo-filter-dialog";

/**
 * The client's channels, their numbers, and who is allowed to call them.
 *
 * Read from the tenant's own connector routes — those already let platform
 * staff read (`isClientMember`), and have since long before this screen. The
 * one thing staff *write* here is the geo-filter, which is ours rather than the
 * client's (every inbound call it lets through is a Twilio leg plus a full
 * pipeline, on our invoice), and it goes through the same dialog the
 * cross-client telephony page uses.
 */
export function ClientChannelsTab() {
  const clientId = useClientId();
  const { data: connectors = [], isLoading } = useConnectors(clientId);
  const { data: geoFilters = [] } = useAdminConnectorGeoFilters();
  const [editing, setEditing] = useState<AdminConnectorGeoFilter | null>(null);

  const geoByConnector = new Map(
    geoFilters
      .filter((entry) => entry.client_id === clientId)
      .map((entry) => [entry.id, entry]),
  );

  if (isLoading) return <Skeleton className="h-40 w-full" />;

  if (connectors.length === 0)
    return (
      <p className="text-muted-foreground text-sm">
        Aucun canal sur ce client.
      </p>
    );

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <th className="px-3 py-2 font-medium">Canal</th>
              <th className="px-3 py-2 font-medium">État</th>
              <th className="px-3 py-2 font-medium">Numéros</th>
              <th className="px-3 py-2 font-medium">Pays autorisés</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {connectors.map((connector) => {
              const geo = geoByConnector.get(connector.id!);
              const filter = geo?.geo_filter;
              const numbers = connector.configuration?.phone_numbers || [];

              return (
                <tr key={connector.id} className="border-t align-top">
                  <td className="px-3 py-2">
                    {/*
                      A connector has no name of its own — what identifies it to
                      a human is its type and the numbers on it, which is also
                      how the client's own channels page names it.
                    */}
                    <div className="font-medium">{connector.type}</div>
                    <div className="text-muted-foreground font-mono text-xs">
                      {connector.id}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <Badge
                      variant={
                        connector.status === "active"
                          ? "secondary"
                          : "destructive"
                      }
                    >
                      {connector.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">
                    {numbers.length === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <div className="space-y-0.5">
                        {numbers.map((number) => (
                          <div key={number} className="font-mono text-xs">
                            {number}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {!filter || filter.countries?.length === 0 ? (
                      <span className="text-muted-foreground text-xs">
                        Tous les pays
                      </span>
                    ) : (
                      <div className="space-y-0.5 text-xs">
                        <div className="font-mono">
                          {filter.countries.join(" ")}
                        </div>
                        {filter.allow_unknown && (
                          <div className="text-muted-foreground">
                            Numéros inconnus acceptés
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {/*
                      Only offered for a connector the cross-client list knows
                      about: that list is what the write route addresses, and a
                      button that cannot be wired to anything is worse than no
                      button.
                    */}
                    {geo && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditing(geo)}
                      >
                        Filtrer les pays
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <GeoFilterDialog connector={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

/**
 * The client's members and their access, as a tab.
 *
 * A wrapper rather than a copy: `TenantUsers` is the panel the provisioning
 * screen already shows, and it takes the tenant as a prop — the only thing
 * missing here was somebody to tell it which one.
 */
export function AdminClientUsersTab() {
  const clientId = useClientId();
  return <TenantUsers tenantId={clientId} />;
}
